import {
  fetchWithErrorHandling,
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {useQuery, useQueryClient} from '@github-ui/react-query'
import {useInvalidateOnAliveUpdate} from '@github-ui/use-invalidate-on-alive-update'
import {useCallback, useEffect, useMemo} from 'react'

import {
  AGENT_MERGE_ENROLLMENT_POLL_INTERVAL_MS,
  shouldPollAgentMergeEnrollment,
} from '../../helpers/agent-merge-enrollment-status'
import {
  AgentMergeEnrollmentLoadError,
  AgentMergePolicyDisabledError,
  normalizeAgentMergeConfiguration,
  normalizeAgentMergeEnrollmentAttempt,
  normalizeAgentMergePolicy,
  unresolvedAgentMergeEnrollment,
  type AgentMergeConfigurationResponse,
  type AgentMergeErrorResponse,
} from '../payloads/agent-merge'
import {claimQueryAnnouncement} from './claim-query-announcement'

export function agentMergeQueryKey(apiURL: string, currentHeadSha: string) {
  return [...agentMergeQueryKeyPrefix(apiURL), currentHeadSha] as const
}

export function agentMergeQueryKeyPrefix(apiURL: string) {
  return [PageData.agentMerge, apiURL] as const
}

export function agentMergeMutationKeyPrefix(apiURL: string) {
  return ['updateAgentMerge', apiURL] as const
}

export function agentMergeMutationKey(apiURL: string, currentHeadSha: string) {
  return [...agentMergeMutationKeyPrefix(apiURL), currentHeadSha] as const
}

/**
 * Refetches the configuration when Alive reports activity on this pull request.
 *
 * The configuration is shared state: any writer can assign, reconfigure or unassign Copilot, and
 * everyone else renders from this cache. Without this, `staleTime: Infinity` would leave the
 * read-only view showing a stale owner and stale actions until the viewer reloaded the page.
 *
 * Subscribes to `pullRequestChannel` for the same reason the session query does: dotcom publishes
 * Agent Merge activity there, while the merge box's other channels carry unrelated events.
 *
 * Invalidation refetches active queries regardless of `staleTime`, so the query keeps `Infinity`
 * and the protection it buys (see `useAgentMergePageData`).
 */
export function useInvalidateAgentMergeConfigurationOnAliveUpdate(
  pullRequestChannel: string | null | undefined,
  currentHeadSha: string,
) {
  const apiURL = usePageDataUrl(PageData.agentMerge)
  const queryClient = useQueryClient()
  const queryKey = useMemo(() => agentMergeQueryKey(apiURL, currentHeadSha), [apiURL, currentHeadSha])

  // A refetch that overlaps a write can resolve after the POST and briefly show the pre-write
  // configuration. Skipping while a write is pending avoids that flicker; it cannot drop an update,
  // because every write ends by invalidating this query itself (see `onSettled`).
  const filter = useCallback(
    () => queryClient.isMutating({mutationKey: agentMergeMutationKeyPrefix(apiURL)}) === 0,
    [apiURL, queryClient],
  )

  useInvalidateOnAliveUpdate(pullRequestChannel, queryKey, {filter})
}

export function useAgentMergePageData({currentHeadSha, enabled}: {currentHeadSha: string; enabled: boolean}) {
  const apiURL = usePageDataUrl(PageData.agentMerge)
  const queryClient = useQueryClient()
  const claimAnnouncement = useCallback(
    (transition: string) => claimQueryAnnouncement(queryClient, agentMergeQueryKey(apiURL, currentHeadSha), transition),
    [apiURL, currentHeadSha, queryClient],
  )

  // A write in flight must never be overridden by a background poll resolving an in-flight
  // enrollment attempt; the write's own `onSettled` refetch is the sole source of truth for it.
  const isMutating = useCallback(
    () => queryClient.isMutating({mutationKey: agentMergeMutationKeyPrefix(apiURL)}) > 0,
    [apiURL, queryClient],
  )

  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister, @github-ui/github-monorepo/require-query-schema -- This head-keyed JSON configuration must refetch on head changes and has no protobuf contract.
  const query = useQuery({
    queryKey: agentMergeQueryKey(apiURL, currentHeadSha),
    enabled,
    retry: false,
    // Keep the head-keyed value fresh (matching use-merge-box-page-data.ts) so a surface
    // mounting during a pending save reuses the cache instead of firing a background GET
    // that could resolve after the POST and clobber the just-saved configuration. A new head
    // produces a new query key, so this never serves data for a stale head. Another user's
    // changes still arrive: `useInvalidateAgentMergeConfigurationOnAliveUpdate` invalidates this
    // query, and invalidation refetches active queries whatever their stale time.
    staleTime: Infinity,
    // A background enrollment attempt is the only reason to poll: once it resolves (or expires)
    // the backend stops sending it and `staleTime: Infinity` takes back over. Bounded by the
    // attempt's own deadline, so polling cannot outlive the placeholder it drives.
    refetchInterval: ({state}) =>
      !isMutating() &&
      shouldPollAgentMergeEnrollment(unresolvedAgentMergeEnrollment(state.data, state.error), Date.now())
        ? AGENT_MERGE_ENROLLMENT_POLL_INTERVAL_MS
        : false,
    refetchIntervalInBackground: false,
    queryFn: async () => {
      const response = await fetchWithErrorHandling(apiURL)
      const json = (await parseJSONWithBetterErrors(response)) as AgentMergeConfigurationResponse &
        AgentMergeErrorResponse
      if (!response.ok && json.automationsPolicyDisabled === true) {
        throw new AgentMergePolicyDisabledError(
          json.error || 'Agent Merge settings could not be loaded.',
          normalizeAgentMergePolicy(json),
        )
      }
      if (response.status === 502 || response.status === 503) {
        const attempt = normalizeAgentMergeEnrollmentAttempt(json.automaticEnrollment)
        if (attempt) {
          throw new AgentMergeEnrollmentLoadError(json.error || 'Agent Merge settings could not be loaded.', attempt)
        }
      }
      throwErrorsIfBadResponse(response, json)
      // Persisted configuration remains valid when CMC's stored head predates the current PR head.
      // The head-keyed query prevents an in-flight response for a previous head from replacing this data.
      return normalizeAgentMergeConfiguration(json)
    },
  })

  const {refetch, data} = query
  const attempt = unresolvedAgentMergeEnrollment(data, query.error)
  // Mirrors `useAgentMergeActivity`'s reconciliation: a pending enrollment can resolve while this
  // tab is backgrounded or offline, so regaining focus/connectivity should check immediately
  // instead of waiting for the next bounded poll tick.
  useEffect(() => {
    if (!attempt) return
    const reconcile = () => {
      if (document.visibilityState !== 'visible') return
      if (isMutating()) return
      void refetch()
    }
    window.addEventListener('focus', reconcile)
    window.addEventListener('online', reconcile)
    document.addEventListener('visibilitychange', reconcile)
    return () => {
      window.removeEventListener('focus', reconcile)
      window.removeEventListener('online', reconcile)
      document.removeEventListener('visibilitychange', reconcile)
    }
  }, [attempt, isMutating, refetch])

  return {...query, claimAnnouncement}
}
